import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import { RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth'
import { authApi } from '../../api/auth'
import { auth, firebaseEnabled } from '../../lib/firebase'
import { useAuthStore } from '../../store/authStore'

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier
    confirmationResult?: ConfirmationResult
  }
}

type Step = 'phone' | 'otp'

const COMMUNITY_ACK_KEY = 'ahirwal_community_ack'

export default function LoginPage() {
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)

  const [step, setStep] = useState<Step>('phone')
  const [showAck, setShowAck] = useState(false)
  // The OTP SMS comes from a short code and some phones file it under Spam (seen on a OnePlus,
  // 2026-10-23). After 20 s on the OTP step without a code, say where to look.
  const [showSpamHint, setShowSpamHint] = useState(false)
  useEffect(() => {
    if (step !== 'otp') { setShowSpamHint(false); return }
    const t = setTimeout(() => setShowSpamHint(true), 20000)
    return () => clearTimeout(t)
  }, [step])

  // Sent here by the API client after a dead session (refresh token rejected): say why,
  // the same way the Android/iOS apps do, instead of a silent bounce to the login form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('expired')) {
      toast.error('Your session has expired. Please log in again.', { duration: 6000 })
      window.history.replaceState(null, '', '/login')
    }
  }, [])

  useEffect(() => {
    if (!sessionStorage.getItem(COMMUNITY_ACK_KEY)) {
      setShowAck(true)
    }
  }, [])

  const dismissAck = () => {
    sessionStorage.setItem(COMMUNITY_ACK_KEY, '1')
    setShowAck(false)
  }
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [useFirebase, setUseFirebase] = useState(false)

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    const normalized = phone.startsWith('+91') ? phone : `+91${phone}`
    // Test accounts (+9190000000xx) always use the backend OTP (hardcoded code).
    const isTestPhone = normalized.startsWith('+919000000')
    setLoading(true)
    try {
      if (firebaseEnabled && auth && !isTestPhone) {
        // Firebase Phone Auth — Google sends & verifies the SMS (no DLT). Fall back
        // to the backend OTP if Firebase can't run (e.g. domain not yet authorised).
        try {
          if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' })
          }
          window.confirmationResult = await signInWithPhoneNumber(auth, normalized, window.recaptchaVerifier)
          setUseFirebase(true)
        } catch (fbErr: any) {
          // Surface the real reason instead of hiding it — e.g. auth/unauthorized-domain,
          // or "OPERATION_NOT_ALLOWED : region not enabled" (Firebase SMS region policy).
          console.error('[login] Firebase phone auth failed:', fbErr?.code, fbErr?.message, fbErr)
          // reCAPTCHA verifiers are single-use; reset it so a retry gets a fresh token.
          try { window.recaptchaVerifier?.clear() } catch { /* noop */ }
          window.recaptchaVerifier = undefined
          // Fall back to the backend OTP path.
          await authApi.sendOtp(normalized)
          setUseFirebase(false)
        }
      } else {
        await authApi.sendOtp(normalized)
        setUseFirebase(false)
      }
      toast.success('OTP sent!')
      setPhone(normalized)
      setStep('otp')
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not send OTP. Check the number and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      let res
      if (useFirebase && window.confirmationResult) {
        const cred = await window.confirmationResult.confirm(otp)
        const idToken = await cred.user.getIdToken()
        res = await authApi.firebaseLogin(idToken)
      } else {
        res = await authApi.verifyOtp(phone, otp)
      }
      const { accessToken, refreshToken, userId, isNewUser } = res.data.data
      setTokens(accessToken, refreshToken, userId)
      if (isNewUser) {
        toast.success('Welcome! Please complete your profile.')
        navigate('/profile/edit?new=1')
      } else {
        toast.success('Welcome back!')
        navigate('/dashboard')
      }
    } catch {
      toast.error('Invalid OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center px-6"
      style={{ background: 'radial-gradient(120% 90% at 50% 0%, #7A1B2E 0%, #4E0F1F 55%, #3A0A18 100%)' }}
    >
      {/* Invisible reCAPTCHA target for Firebase Phone Auth */}
      <div id="recaptcha-container" />

      {/* Community acknowledgement modal */}
      {showAck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-maroon-900/70 backdrop-blur-sm">
          <div className="bg-ivory-25 rounded-3xl shadow-float border border-gold-500/40 max-w-sm w-full p-8 flex flex-col items-center text-center gap-5">
            <div className="w-16 h-16 rounded-full border border-gold-500/60 flex items-center justify-center text-3xl">
              🪷
            </div>
            <div>
              <h2 className="font-display font-semibold text-2xl text-ink-900 mb-2">Ahirwal Matrimony</h2>
              <p className="text-ink-600 text-sm leading-relaxed">
                This platform is exclusively for{' '}
                <span className="font-semibold text-ink-900">Yadavs (Ahirs) of the Ahirwal region</span>
                {' '}— including families from
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {['Delhi', 'Haryana', 'Rajasthan', 'NCR Region'].map((region) => (
                <span
                  key={region}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-maroon-700 px-3 py-1.5 rounded-full border border-gold-500/40 bg-ivory-100"
                >
                  <MapPin size={10} />
                  {region}
                </span>
              ))}
            </div>
            <p className="text-xs text-ink-400">
              By continuing, you confirm that you belong to the Ahirwal Yadav (Ahir) community.
            </p>
            <button
              onClick={dismissAck}
              className="btn-primary w-full"
            >
              I belong to this community
            </button>
          </div>
        </div>
      )}

      <div className="w-full max-w-sm flex flex-col flex-1">
        {/* Top ornament */}
        <p className="text-center text-gold-500 text-[11px] tracking-[0.6em] pt-14">❈ ❈ ❈</p>

        {/* Brand block */}
        <div className="flex-1 flex flex-col justify-center py-8">
          <div
            className="w-24 h-24 mx-auto rounded-full border border-gold-500/60 flex items-center justify-center text-[40px]"
            style={{ boxShadow: '0 0 40px rgba(201,164,76,0.13) inset, 0 0 60px rgba(201,164,76,0.12)' }}
          >
            🪷
          </div>
          <h1 className="font-display font-semibold text-[38px] leading-tight text-champagne-bright text-center mt-7">
            Ahirwal Matrimony
          </h1>
          <p className="text-center text-gold-500 text-xs font-semibold tracking-[0.32em] uppercase mt-3">
            शुभ विवाह
          </p>
          <div className="gold-rule mt-8 mx-2">
            <span>Trusted by Ahirwal families</span>
          </div>
        </div>

        {/* Ivory card */}
        <div className="bg-ivory-25 rounded-3xl border border-gold-500/40 shadow-float p-6 mb-12">
          {step === 'phone' ? (
            <form onSubmit={handleSendOtp}>
              <h3 className="font-display font-semibold text-[22px] text-ink-900">Welcome</h3>
              <p className="text-ink-400 text-xs mt-1">Sign in with your mobile number</p>
              <div className="mt-5 flex rounded-[14px] overflow-hidden border border-ivory-300 bg-white focus-within:border-gold-500 focus-within:shadow-glow transition-all">
                <span className="inline-flex items-center px-4 bg-ivory-100 text-maroon-700 text-sm font-semibold border-r border-ivory-300 flex-shrink-0">
                  +91
                </span>
                <input
                  type="tel"
                  className="flex-1 bg-transparent px-4 py-3.5 text-sm font-medium text-ink-900 placeholder:text-ink-400/70 focus:outline-none"
                  placeholder="10-digit mobile number"
                  value={phone.replace('+91', '')}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  maxLength={10}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full mt-4"
                disabled={loading || phone.replace('+91', '').length !== 10}
              >
                {loading ? 'Sending…' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <h3 className="font-display font-semibold text-[22px] text-ink-900">Verify</h3>
              <p className="text-ink-400 text-xs mt-1">
                Sent to {phone}.{' '}
                <button
                  type="button"
                  className="text-maroon-700 font-semibold hover:underline"
                  onClick={() => setStep('phone')}
                >
                  Change
                </button>
              </p>
              <input
                type="text"
                className="input text-center text-[22px] tracking-[0.5em] mt-5"
                placeholder="······"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                autoFocus
              />
              {showSpamHint && otp.length === 0 && (
                <p className="text-ink-400 text-xs mt-3" data-testid="otp-spam-hint">
                  No SMS yet? Check the Spam or Blocked folder in your Messages app — the code comes from a short number.
                </p>
              )}
              <button
                type="submit"
                className="btn-primary w-full mt-4"
                disabled={loading || otp.length !== 6}
              >
                {loading ? 'Verifying…' : 'Verify OTP'}
              </button>
            </form>
          )}
          <p className="text-center text-[10px] text-ink-400/80 mt-5">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  )
}
